/**
 * Small pure helpers for RefClick UI.
 * Kept together because none of them are big enough to earn their own file.
 */

/** Deterministic monogram tint (HSL for guaranteed dark-bg legibility). */
export function monogramStyle(name) {
  const seed = [...(name || "?")].reduce(
    (acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0,
    7,
  );
  const hue = seed % 360;
  return {
    background: `linear-gradient(135deg, hsl(${hue} 70% 55% / 0.85), hsl(${(hue + 40) % 360} 70% 45% / 0.85))`,
    boxShadow: `0 6px 18px -8px hsl(${hue} 70% 55% / 0.6)`,
  };
}

/** First two visible characters of a name, uppercased. */
export function monogramText(name) {
  if (!name) return "?";
  const cleaned = name.trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Legacy relative-time label — coarse buckets that don't tick.
 * Kept for anywhere in the tree that doesn't have access to `useNow()`.
 * Prefer `formatElapsed()` inside components that use the TickProvider.
 */
export function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 45) return "just now";
  if (seconds < 90) return "1m ago";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

/**
 * Live-ticking relative-time label. Callers pass `now` (from useNow())
 * so every card re-renders on the shared 1-second interval.
 *
 * Format ladder:
 *   < 60 s        → "12s ago"
 *   < 60 min      → "1m 23s ago"
 *   < 24 h        → "1h 05m ago"
 *   yesterday     → "yesterday"
 *   older         → e.g. "12 Feb"
 *
 * Returns null when `iso` is falsy — callers show "No clicks yet".
 */
export function formatElapsed(iso, now = Date.now()) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;

  const secs = Math.max(0, Math.floor((now - then) / 1000));
  if (secs < 60) return `${secs}s ago`;

  const totalMin = Math.floor(secs / 60);
  if (totalMin < 60) {
    return `${totalMin}m ${String(secs % 60).padStart(2, "0")}s ago`;
  }

  const totalHr = Math.floor(totalMin / 60);
  if (totalHr < 24) {
    return `${totalHr}h ${String(totalMin % 60).padStart(2, "0")}m ago`;
  }

  // Beyond 24h — fall back to Dhaka-context calendar labels.
  const daysAgo = Math.floor(totalHr / 24);
  if (daysAgo === 1) return "yesterday";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Dhaka",
      day: "2-digit",
      month: "short",
    }).format(new Date(then));
  } catch {
    return `${daysAgo}d ago`;
  }
}

/** Basic http/https URL validation used before hitting the API. */
export function isValidHttpUrl(value) {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Current hour (0-23) in Asia/Dhaka, computed from the client clock via
 * Intl. Used by the streak-saver nudge (only nudges past 20:00 Dhaka).
 * Not a source of truth for daily counts — those are server-side.
 */
export function getDhakaHour() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dhaka",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const h = parts.find((p) => p.type === "hour")?.value ?? "0";
  return parseInt(h, 10);
}

/**
 * Copy text to the clipboard with a broad fallback for non-secure contexts.
 * Returns `true` on success, `false` otherwise (never throws).
 */
export async function copyToClipboard(text) {
  if (text == null) return false;
  const s = String(text);
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      window.isSecureContext
    ) {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch {
    // fall through to legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = s;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Available themes — order = picker UI order. Default = sunset-amber. */
export const THEMES = [
  { id: "sunset-amber", label: "Sunset Amber", swatch: "#ff8c00" },
  { id: "coral", label: "Coral Glow", swatch: "#ff6b6b" },
  { id: "cyan-violet", label: "Cyan · Violet", swatch: "#00f0ff" },
  { id: "aurora", label: "Aurora", swatch: "#00ff9d" },
  { id: "gamified", label: "Gamified", swatch: "#ffb000" },
  { id: "minimal-luxe", label: "Minimal Luxe", swatch: "#e32636" },
];

/** Suggested chip presets for custom fields in the link detail sheet. */
export const CUSTOM_FIELD_PRESETS = [
  "Email",
  "Username",
  "Password",
  "Notes",
];

/**
 * True when the custom-field's label semantically identifies sensitive
 * data. Matched case-insensitively — `password`, `Pass`, `Passcode`,
 * `PIN`. Card chips must NEVER render these; LinkDetail masks them
 * behind the PIN unlock.
 */
export function isSensitiveLabel(label) {
  if (!label) return false;
  const l = String(label).trim().toLowerCase();
  return l === "password" || l === "pass" || l === "passcode" || l === "pin";
}
